import { create } from 'zustand'

export const TRANSCRIPT_STATUS = {
  /** No STT credentials configured — the UI keeps its disabled treatment. */
  unavailable: 'unavailable',
  /** Not transcribing: call not connected yet. (Follower tabs don't stay here
   *  — the leader's status arrives via the cross-tab mirror.) */
  idle: 'idle',
  connecting: 'connecting',
  live: 'live',
  error: 'error',
} as const

export type TranscriptStatus =
  (typeof TRANSCRIPT_STATUS)[keyof typeof TRANSCRIPT_STATUS]

export interface TranscriptLine {
  id: string
  text: string
  /** Utterance start, ms from the beginning of the capture. */
  startMs: number
}

/**
 * The live call transcript, held outside the on-call view so minimizing the
 * call (which unmounts that view) neither wipes the lines nor stops the
 * engine. Written by `useLiveTranscriptEngine` (mounted once at the panel
 * root) — or, in tabs without the call audio, by the cross-tab mirror — and
 * read by the on-call transcript UI.
 */
interface LiveTranscriptState {
  status: TranscriptStatus
  /** Transcription is on demand: false until the agent starts it for this
   *  call, and reset to false when a new call begins. */
  enabled: boolean
  /** Finalised caller utterances, oldest first. */
  lines: TranscriptLine[]
  /** The in-flight partial utterance; '' when none. */
  interim: string
  setStatus: (status: TranscriptStatus) => void
  setEnabled: (enabled: boolean) => void
  appendLine: (line: TranscriptLine) => void
  setInterim: (interim: string) => void
  /** Clear the transcript for a new call (or absence of one). */
  resetForCall: (status: TranscriptStatus) => void
}

export const useLiveTranscriptStore = create<LiveTranscriptState>((set) => ({
  status: TRANSCRIPT_STATUS.idle,
  enabled: false,
  lines: [],
  interim: '',
  setStatus: (status) => set({ status }),
  setEnabled: (enabled) => set({ enabled }),
  appendLine: (line) =>
    set((state) => ({ lines: [...state.lines, line], interim: '' })),
  setInterim: (interim) => set({ interim }),
  resetForCall: (status) =>
    set({ status, enabled: false, lines: [], interim: '' }),
}))

interface LiveTranscriptView {
  status: TranscriptStatus
  lines: TranscriptLine[]
  interim: string
}

/** Read-only view of the live transcript for the on-call UI. */
export function useLiveTranscript(): LiveTranscriptView {
  const status = useLiveTranscriptStore((state) => state.status)
  const lines = useLiveTranscriptStore((state) => state.lines)
  const interim = useLiveTranscriptStore((state) => state.interim)
  return { status, lines, interim }
}

/** Whether the agent has turned transcription on for the current call. */
export function useTranscriptEnabled(): boolean {
  return useLiveTranscriptStore((state) => state.enabled)
}
